/**
 * ReviewIntentChooser — the single, role-aware "What would you like
 * help improving?" gate.
 *
 * Operator brief (Feb 13, 2026 — Phase A):
 *   "The platform is not selling AI processing. It is selling
 *    self-improvement, confidence, independent learning, driving
 *    insight, coaching awareness, readiness understanding, and
 *    accelerated progress. The UX must communicate this clearly
 *    and emotionally."
 *
 * Behaviour:
 *   • Reads the locked catalogue from /api/review-intents/catalogue
 *     (so wording / role-priority lives server-side and can't drift).
 *   • Shows ALL 5 review types. Cards primary-for-role get the
 *     premium emphasis (size, glow, "for you" tag); the rest are
 *     visible but quieter — "see the broader capability".
 *   • Selecting a card routes onward with `intent=<id>` so the next
 *     step (upload modal / training mode / pupil request modal) can
 *     preselect the right downstream flow.
 *
 * Mobile-first. Cards are large, tappable, with calm gradients.
 * No "Upload" or "AI Review" vocabulary on this surface.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Compass, Trophy, GraduationCap, Car,
  Award, Lock, Sparkles, Loader2, CheckCircle2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon lookup for the icon names that come from the backend catalogue
// (kept here so the server only sends a stable string).
const ICON_FOR = {
  compass: Compass,
  trophy: Trophy,
  "graduation-cap": GraduationCap,
  car: Car,
  award: Award,
};

const TONE_PRIMARY = {
  indigo:  "from-indigo-600 to-indigo-800 ring-indigo-400/30",
  rose:    "from-rose-600 to-rose-800 ring-rose-400/30",
  emerald: "from-emerald-600 to-emerald-800 ring-emerald-400/30",
  amber:   "from-amber-500 to-amber-700 ring-amber-400/30",
  violet:  "from-violet-600 to-violet-800 ring-violet-400/30",
};

const TONE_SECONDARY = {
  indigo:  "border-indigo-300/40 bg-indigo-50/60 text-indigo-900",
  rose:    "border-rose-300/40 bg-rose-50/60 text-rose-900",
  emerald: "border-emerald-300/40 bg-emerald-50/60 text-emerald-900",
  amber:   "border-amber-300/40 bg-amber-50/60 text-amber-900",
  violet:  "border-violet-300/40 bg-violet-50/60 text-violet-900",
};


export default function ReviewIntentChooser() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [catalogue, setCatalogue] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Optional URL hint — a deep-link can ask us to preselect a card.
  const preselect = params.get("intent");

  useEffect(() => {
    let alive = true;
    if (!MEDIA_FEATURE_ENABLED) {
      // Media quarantine — page is route-quarantined too. Defensive pin.
      setCatalogue([]);
      setLoading(false);
      return () => { alive = false; };
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/api/review-intents/catalogue`,
          { withCredentials: true });
        if (!alive) return;
        const intents = r.data?.intents || [];
        setCatalogue(intents);
        if (preselect && intents.some((i) => i.id === preselect)) {
          setSelected(preselect);
        }
      } catch {
        if (alive) setCatalogue([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [preselect]);

  const primary = useMemo(
    () => (catalogue || []).filter((i) => i.primary_for_role),
    [catalogue]
  );
  const secondary = useMemo(
    () => (catalogue || []).filter((i) => !i.primary_for_role),
    [catalogue]
  );

  const handleUnlock = async () => {
    if (!selected) return;
    setSubmitting(true);
    const role = (user?.role || "").toLowerCase();
    // Routing rules (Phase A — operator: "centralised intent system
    // with intelligent shortcuts into preselected modes"):
    //   independent_drive / mock_test → Training Mode-style upload
    //     for pupils (no instructor needed); for instructors / PDIs,
    //     fall through to the self-review clip basket modal.
    //   coaching_review →
    //     pupil  → existing RequestAIReviewModal-style flow with
    //              intent preselected.
    //     adi/pdi/admin → instructor work hub.
    //   general_drive → pupil:  /learn (open RequestAIReviewModal),
    //                   instructor: /work (self-review entry).
    //   pdi_part3 → /pdi/mock-part3 (existing) with intent stamped.
    const intent = selected;
    let dest = "/learn";
    if (intent === "independent_drive" || intent === "mock_test") {
      if (role === "learner") dest = `/training?intent=${intent}`;
      else dest = `/work?intent=${intent}`;
    } else if (intent === "coaching_review") {
      if (role === "learner") dest = `/learn?intent=${intent}`;
      else dest = `/work?intent=${intent}`;
    } else if (intent === "pdi_part3") {
      dest = `/pdi/mock-part3?intent=${intent}`;
    } else {
      // general_drive
      dest = role === "learner"
        ? `/learn?intent=${intent}`
        : `/work?intent=${intent}`;
    }
    // The destination pages read `intent` from the query string and
    // open the correct downstream modal/flow.
    nav(dest);
    setSubmitting(false);
  };

  return (
    <div
      data-testid="review-intent-chooser-page"
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-32"
    >
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="chooser-back"
            onClick={() => nav(-1)}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-600 dark:text-indigo-400">
              Unlock review
            </p>
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
              What would you like help improving?
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-5 space-y-6">
        {/* Intro */}
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-600 dark:text-indigo-400 mb-1.5">
            Self-training system
          </p>
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-1.5">
            Choose your review intention — the AI tunes everything to match.
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Each review type changes what the AI looks for, how it scores,
            and what you receive. Pick the one that matches what you want
            to improve.
          </p>
        </section>

        {loading && (
          <div data-testid="chooser-loading" className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        )}

        {!loading && (catalogue?.length || 0) === 0 && (
          <div data-testid="chooser-empty" className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
              Review options loading soon
            </p>
            <p className="text-[12px] text-slate-500 mt-1">
              Refresh in a moment — the catalogue is being prepared.
            </p>
          </div>
        )}

        {!loading && primary.length > 0 && (
          <section data-testid="chooser-primary-section">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400 mb-3 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-indigo-500" />
              For you · most relevant
            </p>
            <div className="space-y-3">
              {primary.map((it) => (
                <IntentCard
                  key={it.id}
                  intent={it}
                  selected={selected === it.id}
                  onSelect={() => setSelected(it.id)}
                  variant="primary"
                />
              ))}
            </div>
          </section>
        )}

        {!loading && secondary.length > 0 && (
          <section data-testid="chooser-secondary-section">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400 mb-3">
              Also available on the platform
            </p>
            <div className="space-y-2.5">
              {secondary.map((it) => (
                <IntentCard
                  key={it.id}
                  intent={it}
                  selected={selected === it.id}
                  onSelect={() => setSelected(it.id)}
                  variant="secondary"
                />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Sticky CTA */}
      {selected && (
        <div
          data-testid="chooser-cta-bar"
          className="fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-200 dark:border-slate-800 pb-safe"
        >
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-extrabold">
                Selected
              </p>
              <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">
                {catalogue.find((i) => i.id === selected)?.label}
              </p>
            </div>
            <button
              data-testid="chooser-unlock-btn"
              onClick={handleUnlock}
              disabled={submitting}
              className="h-12 px-5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-extrabold text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {catalogue.find((i) => i.id === selected)?.cta || "Continue"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function IntentCard({ intent, selected, onSelect, variant }) {
  const Icon = ICON_FOR[intent.icon] || Compass;
  const isPrimary = variant === "primary";
  const isLocked = intent.available === false;

  if (isPrimary) {
    const toneCls = TONE_PRIMARY[intent.tone] || TONE_PRIMARY.indigo;
    return (
      <button
        data-testid={`intent-card-${intent.id}`}
        data-variant="primary"
        data-selected={selected || undefined}
        onClick={() => !isLocked && onSelect()}
        disabled={isLocked}
        className={`relative w-full text-left rounded-3xl overflow-hidden p-5 bg-gradient-to-br ${toneCls} ring-2 ring-offset-2 ring-offset-slate-50 dark:ring-offset-[#0c1120] ${
          selected ? "ring-white/80 dark:ring-white/40" : "ring-transparent"
        } ${isLocked ? "opacity-60" : "active:scale-[0.995]"} transition-all`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-white/70 mb-0.5">
              {intent.label}
            </p>
            <h3 className="text-lg font-extrabold text-white leading-tight">
              {intent.headline}
            </h3>
          </div>
          {selected && (
            <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0" />
          )}
        </div>

        <p className="text-[13px] text-white/85 leading-relaxed mb-4">
          {intent.blurb}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
          <div className="rounded-xl bg-white/10 border border-white/15 p-3">
            <p className="text-[9px] uppercase tracking-widest font-extrabold text-white/60 mb-1.5">
              AI analyses
            </p>
            <ul className="space-y-0.5">
              {(intent.ai_analyses || []).slice(0, 4).map((s, i) => (
                <li key={i} className="text-[11px] text-white/90 flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-3">
            <p className="text-[9px] uppercase tracking-widest font-extrabold text-white/60 mb-1.5">
              You receive
            </p>
            <ul className="space-y-0.5">
              {(intent.you_receive || []).slice(0, 4).map((s, i) => (
                <li key={i} className="text-[11px] text-white/90 flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-[11px] font-extrabold text-white/95 inline-flex items-center gap-1.5 mt-3">
          {isLocked ? <Lock className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
          {isLocked ? "Coming soon" : intent.cta}
        </p>
      </button>
    );
  }

  // Secondary tile — visible-but-quiet so users see platform capability.
  const sToneCls = TONE_SECONDARY[intent.tone] || TONE_SECONDARY.indigo;
  return (
    <button
      data-testid={`intent-card-${intent.id}`}
      data-variant="secondary"
      data-selected={selected || undefined}
      onClick={() => !isLocked && onSelect()}
      disabled={isLocked}
      className={`relative w-full text-left rounded-2xl border p-3.5 flex items-start gap-3 ${sToneCls} dark:bg-slate-900 dark:text-slate-200 dark:border-slate-800 ${
        selected ? "ring-2 ring-indigo-500" : ""
      } ${isLocked ? "opacity-70" : "hover:shadow-sm active:scale-[0.99]"} transition-all`}
    >
      <div className="w-9 h-9 rounded-xl bg-white/70 dark:bg-slate-800 border border-slate-200/50 dark:border-slate-700 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-slate-700 dark:text-slate-200" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest font-extrabold opacity-70 mb-0.5 inline-flex items-center gap-1">
          {intent.label}
          {isLocked && <Lock className="w-2.5 h-2.5" />}
        </p>
        <p className="text-[13px] font-extrabold leading-snug">
          {intent.headline}
        </p>
        <p className="text-[11px] opacity-75 mt-1 leading-snug">
          {intent.blurb}
        </p>
      </div>
      {selected && (
        <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-1" />
      )}
    </button>
  );
}
